import * as React from 'react';
import { createUseStyles } from 'react-jss';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { useBreakpointDown } from '@shared/hooks/useBreakpoint';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

import { MainMenu } from './components/MainMenu';
import { UserMenu } from './components/UserMenu';
import { CompanyLogo } from './components/CompanyLogo';
import { ThemeToggle } from './components/ThemeToggle';
import { messages } from './header.messages';

import headerBackground from '@assets/header-background.png';
import { flex } from '@styles/resources/mixins';
import { ThemeProps } from '@styles/theme';

import { getEnv } from 'mobx-state-tree';
import { IStoresEnv } from '@core/storesEnv';
import { useUserProfileUIStore } from '../@core/useStores';
import { Observer } from 'mobx-react';
import { WelcomePopup } from 'Welcome/components';
import { useTheme } from '@mui/material/styles';

const useStyles = createUseStyles((theme: ThemeProps) => ({
    wrapper: {
        minHeight: '90px',
        [theme.down('mobile')]: {
            minHeight: 64,
        },
        display: 'flex',
        color: '#ffffff',
        backgroundColor: theme.colors.violet_darkest,
        backgroundImage: theme.isDark
            ? `linear-gradient(135deg, #0e0e1c 0%, #1a1235 50%, #0e1628 100%)`
            : `url(${headerBackground})`,
        backgroundSize: 'cover',
    },
    container: {
        margin: '0 auto',
        width: '100%',
        ...flex('row', 'space-between', 'center'),
        gap: theme.spacing(4),
        padding: `0 ${theme.spacing(8)}`,
        [theme.down('desktop')]: {
            padding: `0 ${theme.spacing(5)}`,
        },
        [theme.down('tablet')]: {
            padding: `0 ${theme.spacing(4)}`,
        },
        [theme.down('mobile')]: {
            padding: `0 ${theme.spacing(3)}`,
            gap: theme.spacing(2),
        },
    },
    leftSide: {
        ...flex('row', '', 'center'),
        minWidth: 0,
        flex: 1,
        gap: theme.spacing(6),
        [theme.down('tablet')]: {
            gap: theme.spacing(2),
        },
    },
    rightSide: {
        ...flex('row', '', 'center'),
        gap: theme.spacing(2),
        flexShrink: 0,
    },
    mobileMenuButton: {
        color: '#ffffff',
        minWidth: 44,
        minHeight: 44,
        borderRadius: theme.radius,

        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
        },
    },
    drawerContent: {
        width: 'min(320px, 85vw)',
        height: '100%',
        padding: `${theme.spacing(4)} ${theme.spacing(4)} ${theme.spacing(6)}`,
        backgroundColor: theme.colors.violet_darkest,
        backgroundImage: 'linear-gradient(180deg, #1a1235 0%, #0e0e1c 100%)',
        overflowY: 'auto',
    },
    drawerHeader: {
        ...flex('row', 'space-between', 'center'),
        marginBottom: theme.spacing(6),
    },
    drawerClose: {
        color: '#ffffff',
        minWidth: 44,
        minHeight: 44,

        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
        },
    },
}))

export const Header = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const isTabletLayout = useBreakpointDown('tablet');
    const isCompactMenuLayout = useBreakpointDown('desktop');
    const [menuOpen, setMenuOpen] = React.useState(false);

    const { api } = getEnv<IStoresEnv>(useUserProfileUIStore());
    const { auth } = getEnv(api);
    React.useEffect(() => {
        auth.checkWelcomeScreen();
    });

    return (
        <>
            <Observer>{() => {

                const onRequestClose = () => {
                    auth.removeWelcomeScreen();
                }
                const onRequestCloseSave = () => {
                    auth.removeWelcomeScreen();
                    auth.closeWelcomeScreen();
                }

                return (
                    <WelcomePopup isOpen={auth.welcomePopupOpened} onRequestClose={onRequestClose} onRequestCloseSave={onRequestCloseSave}></WelcomePopup>
                )
            }
            }
            </Observer>
            <header className={styles.wrapper}>
                <div className={styles.container}>
                    <section className={styles.leftSide}>
                        {isTabletLayout && (
                            <IconButton
                                className={styles.mobileMenuButton}
                                onClick={() => setMenuOpen(true)}
                                aria-label={messages['header.menu.open']}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}
                        <CompanyLogo />
                        {!isTabletLayout && <MainMenu compact={isCompactMenuLayout} />}
                    </section>
                    <section className={styles.rightSide}>
                        <ThemeToggle />
                        <UserMenu />
                    </section>
                </div>
            </header>
            {isTabletLayout && (
                <Drawer
                    anchor="left"
                    open={menuOpen}
                    onClose={() => setMenuOpen(false)}
                >
                    <div className={styles.drawerContent}>
                        <div className={styles.drawerHeader}>
                            <CompanyLogo />
                            <IconButton
                                className={styles.drawerClose}
                                onClick={() => setMenuOpen(false)}
                                aria-label={messages['header.menu.close']}
                            >
                                <CloseIcon />
                            </IconButton>
                        </div>
                        <MainMenu vertical onNavigate={() => setMenuOpen(false)} />
                    </div>
                </Drawer>
            )}
        </>
    );
}
